// Short plate width calculator of the size guide (sections/pp-size-guide.liquid). Added 2026-10-06
// outside the port source; fold into the theme repo. The legal minimum width of a one-row plate is
// 61 mm a character, less 36 mm for each 1 or I, plus 11 mm, and 22 mm more for each gap between
// groups: 33 mm for the usual two groups (GOV.UK display rules: 11 mm margins, 50 mm characters
// with the 1 and I 14 mm, 11 mm between characters, 33 mm between the groups).

const STANDARD = 520;
const MAX_LENGTH = 9;

// The plate builder's spacing (its registration check, assets/pp-chunk-NBTE624H.js): a registration
// in a UK format is spaced the way the plate shows it, whatever spaces were typed. Anything else,
// including a registration not yet typed in full, gives null.
function spaceRegistration(compact) {
  const current = compact.match(/^([A-HJ-PR-Y]{2})(\d{2})([A-HJ-PR-Z]{3})$/);
  if (current && Number(current[2]) >= 2) return `${current[1]}${current[2]} ${current[3]}`;
  const prefix = compact.match(/^([A-HJ-NPR-TV-Y][1-9]\d{0,2})([A-HJ-PR-Y]{3})$/);
  if (prefix) return `${prefix[1]} ${prefix[2]}`;
  const suffix = compact.match(/^([A-HJ-PR-Y]{3})([1-9]\d{0,2}[A-HJ-NPR-TV-Y])$/);
  if (suffix) return `${suffix[1]} ${suffix[2]}`;
  const q = compact.match(/^(Q[1-9]\d{0,2})([A-HJ-PR-Y]{3})$/) || compact.match(/^(QNI)([1-9]\d{0,3})$/);
  if (q) return `${q[1]} ${q[2]}`;
  const diplomatic = compact.match(/^(\d{3})([DX])(\d{3})$/);
  if (diplomatic) return `${diplomatic[1]} ${diplomatic[2]} ${diplomatic[3]}`;
  const dateless = compact.match(/^([A-PR-Z]{1,3})([1-9]\d{0,3})$/) || compact.match(/^([1-9]\d{0,3})([A-PR-Z]{1,3})$/);
  if (dateless && compact.length <= (/[IZ]/.test(compact) ? 7 : 6)) return `${dateless[1]} ${dateless[2]}`;
  return null;
}

// What the builder's registration box shows for a typed value: capitals, spaced once the format is
// recognised and run together until then.
function format(value) {
  const compact = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  const spaced = compact.length <= 7 ? spaceRegistration(compact) : null;
  return { compact, spaced, shown: (spaced ?? compact).slice(0, MAX_LENGTH) };
}

// The position just after the given number of letters and digits, so the caret keeps its place in
// the registration when a space comes or goes in front of it.
function caretAfter(value, characters) {
  let seen = 0;
  for (let index = 0; index < value.length && characters > 0; index += 1) {
    if (/[A-Z0-9]/.test(value[index])) seen += 1;
    if (seen === characters) return index + 1;
  }
  return characters > 0 ? value.length : 0;
}

class PlateWidth extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.stage = this.querySelector('[data-stage]');
    this.plate = this.querySelector('[data-plate]');
    this.text = this.querySelector('[data-plate-text]');
    this.result = this.querySelector('[data-result]');
    this.list = document.getElementById(this.dataset.list);
    this.example = this.text.textContent;
    // Android keyboards type through a composition; respacing in the middle of one can repeat
    // letters, so the box is respaced when it ends. The drawing follows every keystroke.
    this.input.addEventListener('input', (event) => {
      if (!event.isComposing) this.respace();
      this.update();
    });
    this.input.addEventListener('compositionend', () => this.respace());
    this.input.addEventListener('blur', () => this.respace());
    this.hidden = false;
    this.respace();
    this.update();
  }

  respace() {
    const { input } = this;
    const { shown } = format(input.value);
    if (shown === input.value) return;
    const caret = input.selectionStart ?? input.value.length;
    const typed = format(input.value.slice(0, caret)).compact.length;
    input.value = shown;
    if (document.activeElement === input) {
      const at = caretAfter(shown, typed);
      input.setSelectionRange(at, at);
    }
  }

  update() {
    const { compact, spaced } = format(this.input.value);
    const registration = compact ? spaced : this.example;
    this.stage.classList.toggle('is-pending', !registration);
    this.mark();
    if (!registration) {
      this.result.textContent = compact.length > 7
        ? 'UK registrations have up to 7 characters.'
        : compact.length < 7
          ? 'Keep typing your registration as it appears on your V5C.'
          : 'We cannot recognise this UK registration format. Check it against your V5C or retention document.';
      return;
    }
    const characters = registration.replace(/ /g, '');
    const count = characters.length;
    const narrow = (characters.match(/[1I]/g) || []).length;
    const gaps = registration.split(' ').length - 1;
    const width = count * 61 - narrow * 36 + 11 + gaps * 22;
    this.plate.setAttribute('x', String((STANDARD - width) / 2 + 0.5));
    this.plate.setAttribute('width', String(width - 1));
    this.text.textContent = registration;
    const strong = (copy) => {
      const element = document.createElement('strong');
      element.textContent = copy;
      return element;
    };
    this.result.replaceChildren(
      strong(registration),
      ' needs a Short plate at least ',
      strong(`${width} mm`),
      ` wide, ${STANDARD - width} mm narrower than a Standard plate.`,
    );
    if (gaps === 1) this.mark(count, narrow, registration);
  }

  // Outlines the width in the list that matches, and picks out the registration's layout among
  // the others on it (XYZ 7 is laid out like ABC 2: three letters, a space, then a number).
  mark(count, narrow, registration) {
    if (!this.list) return;
    this.list.querySelectorAll('.is-match').forEach((element) => element.classList.remove('is-match'));
    const card = count ? this.list.querySelector(`[data-n="${count}"][data-m="${narrow}"]`) : null;
    if (!card) return;
    card.classList.add('is-match');
    const layout = (text) => text.replace(/[A-Z]/g, 'L').replace(/\d/g, 'D');
    card.querySelectorAll('.psg-chip').forEach((chip) => {
      chip.classList.toggle('is-match', layout(chip.textContent) === layout(registration));
    });
  }
}

if (!customElements.get('pp-plate-width')) customElements.define('pp-plate-width', PlateWidth);
